/**
 * Dashboard keys for button-built panels (W3 card M1). A panel that moves the DOM focus through plain buttons (the
 * Wire, the Charter Board: `navigateButtons`) subscribes to the shell's `DashNavPort`: up/down move through the
 * buttons, activate presses the focused one, left/right go to the panel's `side` handler (the Wire filter). The
 * dashboard's capture listener has already taken the key, so nothing is dispatched back into the window.
 * UI events only.
 */
import { useEffect, useRef, type RefObject } from 'react'
import type { DashFocusInput, DashNavPort } from '../dashboard/focus'
import { navigateButtons } from './mount'

/** One shell input for a button panel rooted at `root`; true when used. Pure apart from the DOM focus. */
export function buttonNavInput(root: HTMLElement | null, input: DashFocusInput, side?: (dir: -1 | 1) => void): boolean {
  if (root === null) return false
  switch (input) {
    case 'up':
    case 'down':
    case 'activate':
      return navigateButtons(root, input)
    case 'left':
    case 'right':
      if (side === undefined) return false
      side(input === 'left' ? -1 : 1)
      return true
    default:
      return false
  }
}

/** Subscribes the panel at `root` to the shell's focus inputs while mounted (no-op without a port). */
export function useButtonNav(nav: DashNavPort | undefined, root: RefObject<HTMLElement | null>, side?: (dir: -1 | 1) => void): void {
  const sideRef = useRef(side)
  sideRef.current = side
  useEffect(() => {
    if (nav === undefined) return undefined
    return nav.subscribe((input) => buttonNavInput(root.current, input, sideRef.current))
  }, [nav, root])
}
